/**
 * Universal astronomical prayer-time calculator.
 *
 * Implements the algorithm from "Astronomical Algorithms" (Jean Meeus) as
 * adapted by ISNA / Muslim World League / Um Al-Qura and popularised by the
 * Adhan.js library.  No external network calls — works offline anywhere on
 * Earth.
 *
 * Supported calculation methods:
 *   MWL   – Muslim World League          (Fajr 18°, Isha 17°)
 *   ISNA  – Islamic Society of N. America (Fajr 15°, Isha 15°)
 *   Egypt – Egyptian General Authority    (Fajr 19.5°, Isha 17.5°)
 *   Makkah – Umm al-Qura (Saudi)          (Fajr 18.5°, Isha 90-min fixed)
 *   Karachi – University of Islamic Sciences Karachi (Fajr 18°, Isha 18°)
 *   Tehran – Institute of Geophysics (Fajr 17.7°, Isha 14°)
 *   Jafari – Shia Ithna Ashari (Fajr 16°, Isha 14°)
 */

export type CalcMethodKey =
  | "MWL"
  | "ISNA"
  | "Egypt"
  | "Makkah"
  | "Karachi"
  | "Tehran"
  | "Jafari";

export type AsrJuristic = "Standard" | "Hanafi";

export interface CalcMethod {
  readonly nameAr: string;
  readonly nameEn: string;
  readonly fajrAngle: number;
  /** null → use ishaMinutes instead */
  readonly ishaAngle: number | null;
  /** used only when ishaAngle is null */
  readonly ishaMinutes: number;
}

export const CALC_METHODS: Record<CalcMethodKey, CalcMethod> = {
  MWL:     { nameAr: "رابطة العالم الإسلامي",               nameEn: "Muslim World League",                  fajrAngle: 18,   ishaAngle: 17,   ishaMinutes: 0  },
  ISNA:    { nameAr: "الجمعية الإسلامية لأمريكا الشمالية",   nameEn: "Islamic Society of North America",     fajrAngle: 15,   ishaAngle: 15,   ishaMinutes: 0  },
  Egypt:   { nameAr: "الهيئة المصرية العامة للمساحة",        nameEn: "Egyptian General Authority of Survey", fajrAngle: 19.5, ishaAngle: 17.5, ishaMinutes: 0  },
  Makkah:  { nameAr: "أم القرى – مكة المكرمة",              nameEn: "Umm al-Qura, Makkah",                  fajrAngle: 18.5, ishaAngle: null, ishaMinutes: 90 },
  Karachi: { nameAr: "جامعة العلوم الإسلامية – كراتشي",     nameEn: "University of Islamic Sciences, Karachi", fajrAngle: 18, ishaAngle: 18,   ishaMinutes: 0  },
  Tehran:  { nameAr: "معهد الجيوفيزياء – طهران",            nameEn: "Institute of Geophysics, Tehran",      fajrAngle: 17.7, ishaAngle: 14,   ishaMinutes: 0  },
  Jafari:  { nameAr: "المذهب الجعفري",                       nameEn: "Shia Ithna Ashari, Leva Research",     fajrAngle: 16,   ishaAngle: 14,   ishaMinutes: 0  },
};

export const DEFAULT_CALC_METHOD: CalcMethodKey = "MWL";

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

function sin(d: number): number { return Math.sin(d * DEG); }
function cos(d: number): number { return Math.cos(d * DEG); }
function tan(d: number): number { return Math.tan(d * DEG); }
function arcsin(x: number): number { return Math.asin(x) / DEG; }
function arccos(x: number): number { return Math.acos(x) / DEG; }
function arctan2(y: number, x: number): number { return Math.atan2(y, x) / DEG; }
function fix(a: number, b: number): number { a -= b * Math.floor(a / b); return a < 0 ? a + b : a; }
function fixAngle(a: number): number { return fix(a, 360); }
function fixHour(a: number): number { return fix(a, 24); }

// ---------------------------------------------------------------------------
// Julian date
// ---------------------------------------------------------------------------

function julianDate(year: number, month: number, day: number): number {
  if (month <= 2) { year -= 1; month += 12; }
  const A = Math.floor(year / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + B - 1524.5;
}

// ---------------------------------------------------------------------------
// Sun position
// ---------------------------------------------------------------------------

function sunPosition(jd: number): { declination: number; equation: number } {
  const D = jd - 2451545.0;
  const g = fixAngle(357.529 + 0.98560028 * D);
  const q = fixAngle(280.459 + 0.98564736 * D);
  const L = fixAngle(q + 1.915 * sin(g) + 0.02 * sin(2 * g));
  const e = 23.439 - 0.00000036 * D;
  const RA = arctan2(cos(e) * sin(L), cos(L)) / 15;
  const declination = arcsin(sin(e) * sin(L));
  const equation = q / 15 - fixHour(RA);
  return { declination, equation };
}

// ---------------------------------------------------------------------------
// Hour-angle for a given altitude
// ---------------------------------------------------------------------------

function hourAngle(latitude: number, declination: number, targetAlt: number, direction: -1 | 1): number {
  const cosH = (sin(targetAlt) - sin(latitude) * sin(declination)) / (cos(latitude) * cos(declination));
  if (cosH < -1) return direction * 180;
  if (cosH > 1) return 0;
  return direction * arccos(cosH) / 15;
}

// ---------------------------------------------------------------------------
// Asr shadow ratio (Standard = Shafi/Maliki/Hanbali, Hanafi = +1)
// ---------------------------------------------------------------------------

function asrAlt(latitude: number, declination: number, juristic: AsrJuristic): number {
  const shadowFactor = juristic === "Hanafi" ? 2 : 1;
  return Math.atan(1 / (shadowFactor + tan(Math.abs(latitude - declination)))) / DEG;
}

// ---------------------------------------------------------------------------
// Core compute function — returns times as fractional hours (UTC)
// ---------------------------------------------------------------------------

export interface RawPrayerHours {
  fajr: number;
  sunrise: number;
  dhuhr: number;
  asr: number;
  sunset: number;
  maghrib: number;
  isha: number;
}

function computeRawHours(
  year: number, month: number, day: number,
  latitude: number, longitude: number,
  method: CalcMethod,
  juristic: AsrJuristic,
  timezoneOffsetHours: number
): RawPrayerHours {
  const jd = julianDate(year, month, day);
  const { declination, equation } = sunPosition(jd);

  const midday = fixHour(12 - longitude / 15 - equation);

  const sunrise = midday + hourAngle(latitude, declination, -0.8333, -1);
  const sunset  = midday + hourAngle(latitude, declination, -0.8333,  1);
  const fajr    = midday + hourAngle(latitude, declination, -method.fajrAngle, -1);
  const asr     = midday + hourAngle(latitude, declination, asrAlt(latitude, declination, juristic), 1);

  let isha: number;
  if (method.ishaAngle !== null) {
    isha = midday + hourAngle(latitude, declination, -method.ishaAngle, 1);
  } else {
    isha = sunset + method.ishaMinutes / 60;
  }

  return {
    fajr:    fixHour(fajr    + timezoneOffsetHours),
    sunrise: fixHour(sunrise + timezoneOffsetHours),
    dhuhr:   fixHour(midday  + timezoneOffsetHours),
    asr:     fixHour(asr     + timezoneOffsetHours),
    sunset:  fixHour(sunset  + timezoneOffsetHours),
    maghrib: fixHour(sunset  + timezoneOffsetHours),
    isha:    fixHour(isha    + timezoneOffsetHours),
  };
}

// ---------------------------------------------------------------------------
// Format fractional hour → "HH:MM"
// ---------------------------------------------------------------------------

function fmtHour(h: number): string {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface CalcPrayerTimings {
  fajr: string;
  sunrise: string;
  dhuhr: string;
  asr: string;
  maghrib: string;
  isha: string;
}

/**
 * Calculate prayer times for a given location and date.
 *
 * @param latitude  Decimal degrees, positive = North
 * @param longitude Decimal degrees, positive = East
 * @param date      The day to calculate for
 * @param timezoneOffsetHours  UTC offset in hours (e.g. +4 for Muscat, -5 for EST)
 * @param method    Calculation method key (default MWL)
 * @param juristic  Asr juristic school (default Standard = Shafi/Maliki/Hanbali)
 */
export function calcPrayerTimes(
  latitude: number,
  longitude: number,
  date: Date,
  timezoneOffsetHours: number,
  method: CalcMethodKey = DEFAULT_CALC_METHOD,
  juristic: AsrJuristic = "Standard"
): CalcPrayerTimings {
  // Derive the calendar date in the target timezone
  const shifted = new Date(date.getTime() + timezoneOffsetHours * 3600000);
  const year  = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth() + 1;
  const day   = shifted.getUTCDate();

  const raw = computeRawHours(year, month, day, latitude, longitude, CALC_METHODS[method], juristic, timezoneOffsetHours);

  return {
    fajr:    fmtHour(raw.fajr),
    sunrise: fmtHour(raw.sunrise),
    dhuhr:   fmtHour(raw.dhuhr),
    asr:     fmtHour(raw.asr),
    maghrib: fmtHour(raw.maghrib),
    isha:    fmtHour(raw.isha),
  };
}

/**
 * Derive the UTC offset in hours for a given IANA timezone name.
 * Falls back to 0 if the runtime does not support Intl.
 */
export function getTimezoneOffsetHours(ianaTimezone: string, date = new Date()): number {
  try {
    const fmt = new Intl.DateTimeFormat("en", {
      timeZone: ianaTimezone,
      timeZoneName: "shortOffset"
    });
    const parts = fmt.formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    // "GMT+4", "GMT-5:30", "GMT"
    const match = tzPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return 0;
    const sign = match[1] === "+" ? 1 : -1;
    const hours = Number(match[2] ?? 0);
    const minutes = Number(match[3] ?? 0);
    return sign * (hours + minutes / 60);
  } catch {
    return 0;
  }
}
